
import { useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getEdgeFunctionUrl } from '@/utils/supabaseUrl';

interface FormData {
  marketType: string;
  productName: string;
  price: string;
  location: string;
  productRemark: string;
  productCategory: string;
}

interface UseFormSubmissionProps {
  userId: string;
  formData: FormData;
  date: Date;
  onSuccess?: () => void;
}

export const useFormSubmission = ({ userId, formData, date, onSuccess }: UseFormSubmissionProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): string | null => {
    // For Merkato, location is not required (defaults to 'Merkato')
    const needsLocation = formData.marketType !== 'merkato';
    if (!formData.marketType || !formData.productName || !formData.price || (needsLocation && !formData.location)) {
      return 'Please fill in all required fields';
    }

    if (parseFloat(formData.price) <= 0) {
      return 'Price must be greater than 0';
    }

    return null;
  };

  const showAlert = (message: string, callback?: () => void) => {
    const tg = window.Telegram?.WebApp;
    if (tg?.showAlert) {
      tg.showAlert(message, callback);
    } else {
      toast.error(message);
    }
  };

  const showSuccess = (message: string) => {
    const tg = window.Telegram?.WebApp;
    
    // Haptic feedback for success
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.impactOccurred('light');
    }

    if (tg?.showAlert) {
      tg.showAlert(message, () => {
        if (tg.close) {
          tg.close();
        }
      });
    } else {
      toast.success(message);
      onSuccess?.();
    }
  };

  const submitForm = async () => {
    const validationError = validateForm();
    if (validationError) {
      showAlert(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${getEdgeFunctionUrl('benchmark-webview')}?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          market_type: formData.marketType,
          product_name: formData.productName,
          price: parseFloat(formData.price),
          location: formData.marketType === 'merkato' ? 'Merkato' : formData.location,
          date: format(date, 'yyyy-MM-dd'),
          product_remark: formData.productRemark || '',
          product_category: formData.productCategory || ''
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const successMessage = result.message || 'Price entry saved successfully!';
        showSuccess(successMessage);
      } else {
        const errorMessage = result.error || 'Error saving entry. Please try again.';
        showAlert(errorMessage);
      }
    } catch (error) {
      console.error('Network error:', error);
      const errorMessage = 'Network error. Please check your connection and try again.';
      showAlert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    submitForm
  };
};
